import { FC } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { TIME_OPTIONS, type DayOfWeekConfig } from './constants';
import type { AvailabilitySlot } from '../../../../types/therapist-dashboard.types';

export interface DaySlotEditorProps {
  slot: AvailabilitySlot;
  dayConfig: DayOfWeekConfig;
  onToggle: (dayOfWeek: number) => void;
  onTimeChange: (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => void;
}

export const DaySlotEditor: FC<DaySlotEditorProps> = ({
  slot,
  dayConfig,
  onToggle,
  onTimeChange,
}) => {
  const isValidTimeRange = slot.startTime < slot.endTime;

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border p-4 transition-all',
        slot.isAvailable
          ? 'border-green-300 dark:border-green-700 shadow-sm'
          : 'border-gray-200 dark:border-slate-700'
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Day Toggle */}
        <div className="flex items-center justify-between sm:justify-start gap-3 min-w-[140px]">
          <button
            type="button"
            onClick={() => onToggle(slot.dayOfWeek)}
            className={cn(
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full',
              'border-2 border-transparent transition-colors duration-200 ease-in-out',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'dark:focus:ring-offset-slate-800',
              slot.isAvailable ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
            )}
            role="switch"
            aria-checked={slot.isAvailable}
            aria-label={`Toggle availability for ${dayConfig.label}`}
          >
            <span
              className={cn(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full',
                'bg-white shadow ring-0 transition duration-200 ease-in-out',
                slot.isAvailable ? 'translate-x-5' : 'translate-x-0'
              )}
            />
          </button>
          <span
            className={cn(
              'font-medium text-sm sm:text-base',
              slot.isAvailable
                ? 'text-gray-900 dark:text-slate-100'
                : 'text-gray-500 dark:text-slate-400'
            )}
          >
            <span className="hidden sm:inline">{dayConfig.label}</span>
            <span className="sm:hidden">{dayConfig.short}</span>
          </span>
        </div>

        {/* Time Selectors */}
        <div
          className={cn(
            'flex flex-1 items-center gap-2 transition-opacity',
            slot.isAvailable ? 'opacity-100' : 'opacity-50 pointer-events-none'
          )}
        >
          <div className="flex-1 min-w-[100px]">
            <label className="sr-only">Start time for {dayConfig.label}</label>
            <select
              value={slot.startTime}
              onChange={(e) => onTimeChange(slot.dayOfWeek, 'startTime', e.target.value)}
              disabled={!slot.isAvailable}
              className={cn(
                'w-full px-3 py-2 text-sm rounded-md border',
                'bg-white dark:bg-slate-700',
                'text-gray-900 dark:text-slate-100',
                'border-gray-300 dark:border-slate-600',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <span className="text-gray-500 dark:text-slate-400 text-sm font-medium">to</span>

          <div className="flex-1 min-w-[100px]">
            <label className="sr-only">End time for {dayConfig.label}</label>
            <select
              value={slot.endTime}
              onChange={(e) => onTimeChange(slot.dayOfWeek, 'endTime', e.target.value)}
              disabled={!slot.isAvailable}
              className={cn(
                'w-full px-3 py-2 text-sm rounded-md border',
                'bg-white dark:bg-slate-700',
                'text-gray-900 dark:text-slate-100',
                'border-gray-300 dark:border-slate-600',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status indicator */}
        <div className="hidden lg:flex items-center min-w-[100px] justify-end">
          {slot.isAvailable ? (
            !isValidTimeRange ? (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
                <AlertCircle className="w-4 h-4" />
                Invalid range
              </span>
            ) : (
              <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                Available
              </span>
            )
          ) : (
            <span className="text-gray-400 dark:text-slate-500 text-xs">Unavailable</span>
          )}
        </div>
      </div>

      {/* Mobile validation error */}
      {slot.isAvailable && !isValidTimeRange && (
        <div className="lg:hidden mt-2 flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
          <AlertCircle className="w-4 h-4" />
          End time must be after start time
        </div>
      )}
    </div>
  );
};
